import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import './Economy.css';

function Economy({ initialSection = 'leaderboard', showInternalNav = true }) {
  const { hasPermission, apiFetch } = useAuth();
  const { language } = useI18n();
  const [activeSection, setActiveSection] = useState('leaderboard');
  const [view, setView] = useState('top50');
  const [customLimit, setCustomLimit] = useState('');
  const [searchLimit, setSearchLimit] = useState('');
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Money Management State
  const [action, setAction] = useState('give');
  const [identifier, setIdentifier] = useState('');
  const [accountType, setAccountType] = useState('bank');
  const [amount, setAmount] = useState('');
  const [result, setResult] = useState(null);
  const [moneyLoading, setMoneyLoading] = useState(false);
  const [error, setError] = useState(null);

  const t = language === 'da'
    ? {
        leaderboard: 'Topliste',
        manageMoney: 'Administrer penge',
        economyLeaderboard: 'økonomi topliste',
        loadingEconomy: 'Indlæser økonomidata...',
        rank: 'Rang',
        name: 'Navn',
        bank: 'Bank',
        cash: 'Kontant',
        itemCash: 'Kontanter i items',
        totalWealth: 'Samlet formue',
        moneyManagement: 'Pengeadministration',
        giveMoney: 'Giv penge',
        takeMoney: 'Tag penge',
        playerIdentifier: 'Spiller identifikator',
        accountType: 'Kontotype',
        amount: 'Beloeb',
        processing: 'Behandler...',
        error: 'Fejl',
        successGive: 'Penge givet',
        successTake: 'Penge taget',
        player: 'Spiller',
        account: 'Konto',
        previousBalance: 'Tidligere saldo',
        newBalance: 'Ny saldo',
        failedEconomy: 'Kunne ikke hente økonomidata',
        operationFailed: 'Handlingen fejlede',
        bankOption: 'Bank',
        cashOption: 'Kontant',
        blackMoney: 'Sorte penge',
        enterAmount: 'Indtast beloeb'
      }
    : {
        leaderboard: 'Leaderboard',
        manageMoney: 'Manage Money',
        economyLeaderboard: 'Economy Leaderboard',
        loadingEconomy: 'Loading economy data...',
        rank: 'Rank',
        name: 'Name',
        bank: 'Bank',
        cash: 'Cash',
        itemCash: 'Cash in Items',
        totalWealth: 'Total Wealth',
        moneyManagement: 'Money Management',
        giveMoney: 'Give Money',
        takeMoney: 'Take Money',
        playerIdentifier: 'Player Identifier',
        accountType: 'Account Type',
        amount: 'Amount',
        processing: 'Processing...',
        error: 'Error',
        successGive: 'Money Given Successfully',
        successTake: 'Money Taken Successfully',
        player: 'Player',
        account: 'Account',
        previousBalance: 'Previous Balance',
        newBalance: 'New Balance',
        failedEconomy: 'Failed to fetch economy data',
        operationFailed: 'Operation failed',
        bankOption: 'Bank',
        cashOption: 'Cash',
        blackMoney: 'Black Money',
        enterAmount: 'Enter amount'
      };

  const failedEconomyMessage = t.failedEconomy;

  const handleCustomSearch = () => {
    if (customLimit && customLimit.trim()) {
      setSearchLimit(customLimit);
      setView('custom');
    }
  };

  const handleCustomKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCustomSearch();
    }
  };

  const fetchEconomy = useCallback(async () => {
    setLoading(true);
    try {
      let endpoint;
      
      if (view === 'custom' && searchLimit) {
        const limit = Math.min(Math.max(parseInt(searchLimit) || 50, 1), 500);
        endpoint = `/api/economy/top?limit=${limit}`;
      } else if (view === 'top50') {
        endpoint = '/api/economy/top50';
      } else if (view === 'top100') {
        endpoint = '/api/economy/top100';
      } else {
        endpoint = '/api/economy/top50';
      }
      
      const response = await apiFetch(`${endpoint}`);
      if (!response.ok) throw new Error(failedEconomyMessage);
      const data = await response.json();
      setPlayers(data);
    } catch (err) {
      console.error('Error fetching economy:', err);
    } finally {
      setLoading(false);
    }
  }, [view, searchLimit, failedEconomyMessage, apiFetch]);

  useEffect(() => {
    if (activeSection === 'leaderboard') {
      fetchEconomy();
    }
  }, [activeSection, fetchEconomy]);

  useEffect(() => {
    const nextSection = String(initialSection || '').trim().toLowerCase();
    if (nextSection === 'leaderboard' || nextSection === 'manage') {
      setActiveSection(nextSection);
    }
  }, [initialSection]);

  const handleMoneySubmit = async (e) => {
    e.preventDefault();
    setMoneyLoading(true);
    setError(null);
    setResult(null);

    try {
      const endpoint = action === 'give' ? '/api/money/give' : '/api/money/take';
      const response = await apiFetch(`${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, type: accountType, amount: parseInt(amount) })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t.operationFailed);
      }

      const data = await response.json();
      setResult(data);
      
      // Reset form
      setIdentifier('');
      setAmount('');
    } catch (err) {
      setError(err.message);
    } finally {
      setMoneyLoading(false);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <div className="economy-page">
      {showInternalNav && (
        <div className="economy-nav">
          <button
            className={activeSection === 'leaderboard' ? 'active' : ''}
            onClick={() => setActiveSection('leaderboard')}
          >
            {t.leaderboard}
          </button>
          {hasPermission('economy.edit') && (
            <button
              className={activeSection === 'manage' ? 'active' : ''}
              onClick={() => setActiveSection('manage')}
            >
              {t.manageMoney}
            </button>
          )}
        </div>
      )}

      {activeSection === 'leaderboard' && (
        <div className="leaderboard-section">
          <div className="economy-header">
            <h2>{t.economyLeaderboard}</h2>
            <div className="view-toggle">
              <button
                className={view === 'top50' ? 'active' : ''}
                onClick={() => { setView('top50'); setCustomLimit(''); setSearchLimit(''); }}
              >
                Top 50
              </button>
              <button
                className={view === 'top100' ? 'active' : ''}
                onClick={() => { setView('top100'); setCustomLimit(''); setSearchLimit(''); }}
              >
                Top 100
              </button>
              <div className="custom-limit-input">
                <input
                  type="number"
                  min="1"
                  max="500"
                  placeholder={language === 'da' ? 'Eller indtast eget antal...' : 'Or enter custom...'}
                  value={customLimit}
                  onChange={(e) => setCustomLimit(e.target.value)}
                  onKeyPress={handleCustomKeyPress}
                />
                <button
                  className="search-button"
                  onClick={handleCustomSearch}
                  disabled={!customLimit || loading}
                  title={language === 'da' ? 'Søg' : 'Search'}
                >
                  {language === 'da' ? 'Søg' : 'Search'}
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loading">{t.loadingEconomy}</div>
          ) : (
            <div className="economy-table-container">
              <table className="economy-table">
                <thead>
                  <tr>
                    <th>{t.rank}</th>
                    <th>{t.name}</th>
                    <th>{t.bank}</th>
                    <th>{t.cash}</th>
                    <th>{t.itemCash}</th>
                    <th>{t.totalWealth}</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((player) => (
                    <tr key={player.rank} className={player.rank <= 3 ? `rank-${player.rank}` : ''}>
                      <td className="rank-cell">
                        {player.rank <= 3 && <span className="medal">[+]</span>}
                        #{player.rank}
                      </td>
                      <td className="name-cell">{player.name}</td>
                      <td className="money-cell">{formatMoney(player.bank)}</td>
                      <td className="money-cell">{formatMoney(player.cash)}</td>
                      <td className="money-cell" title="Cash items stored in inventory/apartment">{formatMoney(player.itemCash || 0)}</td>
                      <td className="total-cell">{formatMoney(player.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeSection === 'manage' && hasPermission('economy.edit') && (
        <div className="money-management-section">
          <div className="money-header">
            <h2>{t.moneyManagement}</h2>
          </div>

          <div className="action-selector">
            <button
              className={action === 'give' ? 'active give' : 'give'}
              onClick={() => setAction('give')}
            >
              {t.giveMoney}
            </button>
            <button
              className={action === 'take' ? 'active take' : 'take'}
              onClick={() => setAction('take')}
            >
              {t.takeMoney}
            </button>
          </div>

          <form onSubmit={handleMoneySubmit} className="money-form">
            <div className="form-group">
              <label>{t.playerIdentifier}</label>
              <input
                type="text"
                placeholder="char1:xxxxx or license:xxxxx"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>{t.accountType}</label>
              <select value={accountType} onChange={(e) => setAccountType(e.target.value)}>
                <option value="bank">{t.bankOption}</option>
                <option value="money">{t.cashOption}</option>
                <option value="black_money">{t.blackMoney}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t.amount}</label>
              <input
                type="number"
                placeholder={t.enterAmount}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="1"
                required
              />
            </div>

            <button type="submit" className={`submit-button ${action}`} disabled={moneyLoading}>
              {moneyLoading ? t.processing : (action === 'give' ? t.giveMoney : t.takeMoney)}
            </button>
          </form>

          {error && (
            <div className="alert error">
              <strong>{t.error}:</strong> {error}
            </div>
          )}

          {result && (
            <div className={`alert success ${action}`}>
              <h3>{action === 'give' ? t.successGive : t.successTake}</h3>
              <div className="result-details">
                <div className="detail-row">
                  <span>{t.player}:</span>
                  <strong>{result.identifier}</strong>
                </div>
                <div className="detail-row">
                  <span>{t.account}:</span>
                  <strong>{result.type}</strong>
                </div>
                <div className="detail-row">
                  <span>{t.amount}:</span>
                  <strong>{formatMoney(result.amount)}</strong>
                </div>
                <div className="detail-row">
                  <span>{t.previousBalance}:</span>
                  <strong>{formatMoney(result.previousBalance)}</strong>
                </div>
                <div className="detail-row">
                  <span>{t.newBalance}:</span>
                  <strong className="highlight">{formatMoney(result.newBalance)}</strong>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Economy;



